import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, Popconfirm, Tree, message, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SafetyOutlined } from '@ant-design/icons';
import { getRoles, createRole, updateRole, deleteRole, getRolePages, setRolePages, getPageTree } from '../../../services/api';

export default function RoleList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [permRoleId, setPermRoleId] = useState(null);
  const [pageTree, setPageTree] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getRoles();
      setData(res.data || []);
    } catch (err) { message.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editRecord) {
        await updateRole(editRecord.id, values);
      } else {
        await createRole(values);
      }
      message.success('保存成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message);
    }
  };

  const openPermModal = async (roleId) => {
    setPermRoleId(roleId);
    try {
      const [treeRes, pagesRes] = await Promise.all([getPageTree(), getRolePages(roleId)]);
      setPageTree(treeRes.data || []);
      setCheckedKeys(pagesRes.data || []);
      setPermOpen(true);
    } catch (err) { message.error(err.message); }
  };

  const handleSavePerm = async () => {
    try {
      await setRolePages(permRoleId, checkedKeys);
      message.success('权限保存成功');
      setPermOpen(false);
    } catch (err) { message.error(err.message); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRole(id);
      message.success('删除成功');
      fetchData();
    } catch (err) { message.error(err.message); }
  };

  const columns = [
    { title: '角色名称', dataIndex: 'name' },
    { title: '描述', dataIndex: 'description' },
    { title: '用户数', dataIndex: 'userCount' },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => { setEditRecord(r); form.setFieldsValue(r); setModalOpen(true); }}>编辑</Button>
        <Button size="small" icon={<SafetyOutlined />} onClick={() => openPermModal(r.id)}>权限</Button>
        {r.id !== 1 && (
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        )}
      </Space>
    )},
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditRecord(null); form.resetFields(); setModalOpen(true); }}>新增角色</Button>
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" loading={loading} pagination={false} />
      <Modal title={editRecord ? '编辑角色' : '新增角色'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述" rules={[{ required: true, message: '请输入描述' }]}><Input.TextArea /></Form.Item>
        </Form>
      </Modal>
      <Modal title="分配页面权限" open={permOpen} onOk={handleSavePerm} onCancel={() => setPermOpen(false)} width={400} destroyOnClose>
        <Tree checkable treeData={pageTree} checkedKeys={checkedKeys}
          onCheck={(keys) => setCheckedKeys(keys)}
          fieldNames={{ title: 'name', key: 'id', children: 'children' }}
          defaultExpandAll />
      </Modal>
    </div>
  );
}
